import type { SignalSource } from "@app/types/thread_ledger";
import { z } from "zod";

/**
 * The mailbox spine's vocabulary. Every name here is provider-neutral: Gmail is
 * the first adapter, and Outlook (Graph delta + subscriptions) is a second
 * adapter over the same types rather than a second pipeline. Nothing below may
 * mention a Gmail label id or a Graph folder id — those live in the adapters.
 */

export const MAIL_PROVIDERS = [
  "gmail",
] as const satisfies readonly SignalSource[];
export type MailProvider = (typeof MAIL_PROVIDERS)[number];

export const MAILBOX_STATUSES = ["active", "paused", "disconnected"] as const;
export type MailboxStatus = (typeof MAILBOX_STATUSES)[number];

/**
 * The provider's own pre-sort, when it has one. Gmail's inbox tabs and
 * Outlook's Focused/Other are both a free first-pass signal that costs no model
 * call, so the adapter reports it and triage reads it.
 */
export type ProviderCategory =
  | "primary"
  | "promotions"
  | "social"
  | "updates"
  | "forums"
  | null;

export interface MailAddress {
  email: string;
  name: string | null;
}

export interface MailAttachmentRef {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
}

/**
 * One message, decoded once by its adapter. The body is a bounded text sample:
 * triage needs the ask, not the whole reply chain, and a full body is always one
 * tool call away for the agent that needs it.
 */
export interface MailMessage {
  provider: MailProvider;
  messageId: string;
  threadId: string;
  subject: string | null;
  from: MailAddress | null;
  to: MailAddress[];
  cc: MailAddress[];
  replyTo: MailAddress | null;
  sentAt: Date;
  direction: "sent" | "received";
  bodySample: string;
  providerCategory: ProviderCategory;
  isUnread: boolean;
  inInbox: boolean;
  listUnsubscribe: boolean;
  bulkPrecedence: boolean;
  autoSubmitted: boolean;
  hasCalendarInvite: boolean;
  attachments: MailAttachmentRef[];
  deepLink: string | null;
}

/**
 * What a message is, for the person reading their inbox. Deliberately coarse:
 * every category maps to one label and one default action, and a finer
 * taxonomy is what per-user rules are for.
 */
export const TRIAGE_CATEGORIES = [
  "needs_reply",
  "action",
  "scheduling",
  "fyi",
  "receipt",
  "notification",
  "security",
  "newsletter",
  "promotional",
  "cold_outreach",
] as const;
export type TriageCategory = (typeof TRIAGE_CATEGORIES)[number];

/** Categories that never ask anything of the person. */
export const LOW_PRIORITY_CATEGORIES: readonly TriageCategory[] = [
  "newsletter",
  "promotional",
  "cold_outreach",
  "receipt",
  "notification",
];

export const TRIAGE_PRIORITIES = ["urgent", "high", "normal", "low"] as const;
export type TriagePriority = (typeof TRIAGE_PRIORITIES)[number];

/**
 * An open loop, extracted once from the message that opened it.
 *
 * - `ask_of_me`: someone asked the owner for something.
 * - `ask_of_them`: the owner asked someone for something.
 * - `promise_by_me`: the owner said they would do something.
 * - `promise_by_them`: someone said they would do something for the owner.
 *
 * "Waiting on me" is `ask_of_me` + `promise_by_me`; "waiting on them" is the
 * other two. The Loose-Ends Sweep and every follow-up routine are queries over
 * these, never a fresh read of the mailbox.
 */
export const ACTION_ITEM_KINDS = [
  "ask_of_me",
  "ask_of_them",
  "promise_by_me",
  "promise_by_them",
] as const;
export type ActionItemKind = (typeof ACTION_ITEM_KINDS)[number];

export const ACTION_ITEM_STATUSES = ["open", "done", "dismissed"] as const;
export type ActionItemStatus = (typeof ACTION_ITEM_STATUSES)[number];

export interface ActionItem {
  id: string;
  /**
   * `triage` items are closed by the conversation itself (a reply arrives, the
   * owner answers). `agent` items were filed deliberately — one line of a
   * vendor checklist — and only a judgment closes them, never a stray reply.
   */
  origin?: "triage" | "agent";
  kind: ActionItemKind;
  text: string;
  counterpartEmail: string | null;
  dueOn: string | null;
  status: ActionItemStatus;
  sourceMessageId: string;
  openedAt: string;
  closedAt: string | null;
}

export const WAITING_ON_ME_KINDS: readonly ActionItemKind[] = [
  "ask_of_me",
  "promise_by_me",
];

/** How a triage answer was reached, kept so evals can score each stage. */
export type TriageSource = "rule" | "heuristic" | "model";

export interface TriageResult {
  messageId: string;
  category: TriageCategory;
  priority: TriagePriority;
  needsReply: boolean;
  /**
   * A received message saying the sender no longer needs anything from the
   * owner: the issue resolved itself, the ask was withdrawn, someone else
   * handled it. It closes what that sender had asked of the owner, because
   * the owner writing back is not the only way an ask ends.
   */
  settlesAsksOfMe: boolean;
  /**
   * The message delivers or calls off what its writer promised on the thread.
   * Only that closes a promise: a reply that just moves the date, or is about
   * something else, leaves it owed.
   */
  settlesPromises: boolean;
  summary: string | null;
  actionItems: Omit<
    ActionItem,
    "id" | "origin" | "status" | "sourceMessageId" | "openedAt" | "closedAt"
  >[];
  source: TriageSource;
}

/**
 * A person's own sorting rule, learned from a correction ("Canva is always
 * promotional") or set in chat. Matched before any heuristic or model, so a
 * rule is the cheapest and the most authoritative answer the spine has.
 */
export const TriageRuleSchema = z.object({
  id: z.string(),
  match: z.object({
    fromEmail: z.string().optional(),
    fromDomain: z.string().optional(),
    subjectContains: z.string().optional(),
  }),
  category: z.enum(TRIAGE_CATEGORIES),
  priority: z.enum(TRIAGE_PRIORITIES).optional(),
  createdAt: z.string(),
});
export type TriageRule = z.infer<typeof TriageRuleSchema>;

export const TriageModelOutputSchema = z.object({
  messages: z.array(
    z.object({
      message_id: z.string(),
      category: z.enum(TRIAGE_CATEGORIES),
      priority: z.enum(TRIAGE_PRIORITIES),
      needs_reply: z.boolean(),
      settles_asks_of_owner: z
        .boolean()
        .describe(
          "True when this received message says the sender no longer needs anything from the owner on this thread."
        ),
      settles_promises: z
        .boolean()
        .describe(
          "True when this message delivers or calls off what its writer promised earlier on this thread."
        ),
      summary: z
        .string()
        .describe("One sentence: what the message is and what it wants."),
      action_items: z.array(
        z.object({
          kind: z.enum(ACTION_ITEM_KINDS),
          text: z
            .string()
            .describe("The concrete deliverable, standalone, imperative."),
          counterpart_email: z.string().nullable(),
          due_on: z
            .string()
            .nullable()
            .describe("ISO date when the message states one, else null."),
        })
      ),
    })
  ),
});

/**
 * What a routine woken by mail is told about. A wake filter is data, not a
 * predicate, so a template stays serializable and the catalog can describe it.
 */
export interface MailWakeFilter {
  categories?: TriageCategory[];
  needsReply?: true;
  withAttachments?: true;
}

/** One triaged thread, as a routine's payload and the inbox tools render it. */
export interface InboxItem {
  source: SignalSource;
  threadId: string;
  lastMessageId: string | null;
  subject: string | null;
  counterpartEmail: string | null;
  counterpartName: string | null;
  category: TriageCategory | null;
  priority: TriagePriority | null;
  needsReply: boolean;
  summary: string | null;
  hasAttachments: boolean;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  deepLink: string | null;
  actionItems: ActionItem[];
}
